import { supabase } from "@/integrations/supabase/client";
import { newJobCatalogQuery } from "@/lib/catalog/newJobCatalogQuery";
import { getBayRequirementsForCategory } from "@/utils/categoryToBayRequirements";

export interface ResolvedBayRules {
  primary_bay_type: string | null;
  allowed_bay_types: string[];
  required_capabilities: string[];
  excluded_bay_types: string[];
  bay_requirement_source: 'catalog' | 'manual_override' | 'ai_diagnostics';
  is_disabled: boolean;
}

interface CatalogJob {
  id: string;
  primary_bay_type: string | null;
  allowed_bay_types: string[] | null;
  required_capabilities: string[] | null;
  excluded_bay_types: string[] | null;
  [key: string]: any;
}

interface JobOverride {
  primary_bay_type_override: string | null;
  allowed_bay_types_override: string[] | null;
  required_capabilities_override: string[] | null;
  excluded_bay_types_override: string[] | null;
  disabled: boolean;
}

/**
 * Resolves final bay rules for a job by merging global catalog + shop override
 * @param catalogJob - Job from jobs_catalog (global truth)
 * @param override - Optional shop-specific override
 * @returns Resolved bay rules with source tracking
 */
export function resolveJobBayRules(
  catalogJob: CatalogJob,
  override?: JobOverride | null
): ResolvedBayRules {
  // If shop has disabled this job, mark it disabled
  if (override?.disabled) {
    return {
      primary_bay_type: null,
      allowed_bay_types: [],
      required_capabilities: [],
      excluded_bay_types: [],
      bay_requirement_source: 'manual_override',
      is_disabled: true,
    };
  }

  // Merge override with catalog (override takes precedence)
  const hasOverride = !!(
    override?.primary_bay_type_override ||
    override?.allowed_bay_types_override?.length ||
    override?.required_capabilities_override?.length ||
    override?.excluded_bay_types_override?.length
  );

  return {
    primary_bay_type: override?.primary_bay_type_override ?? catalogJob.primary_bay_type,
    allowed_bay_types: override?.allowed_bay_types_override ?? catalogJob.allowed_bay_types ?? [],
    required_capabilities: override?.required_capabilities_override ?? catalogJob.required_capabilities ?? [],
    excluded_bay_types: override?.excluded_bay_types_override ?? catalogJob.excluded_bay_types ?? [],
    bay_requirement_source: hasOverride ? 'manual_override' : 'catalog',
    is_disabled: false,
  };
}

/**
 * Fetches jobs with resolved bay rules for a specific shop
 * @param shopId - Shop ID to fetch overrides for
 * @param filters - Optional filters (category, submenu, etc.)
 * @returns Array of jobs with resolved bay rules
 */
export async function fetchJobsWithResolvedRules(shopId: string, filters?: {
  category_key?: string;
  sub_menu?: string;
  search?: string;
}) {
  // Fetch global catalog jobs — a NEW-selection list, so retired rows are excluded (W2).
  let catalogQuery = newJobCatalogQuery('*');

  if (filters?.category_key) {
    catalogQuery = catalogQuery.eq('category_key', filters.category_key);
  }
  if (filters?.sub_menu) {
    catalogQuery = catalogQuery.eq('sub_menu', filters.sub_menu);
  }
  if (filters?.search) {
    catalogQuery = catalogQuery.or(`label.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
  }

  const { data: catalogJobs, error: catalogError } = await catalogQuery;
  if (catalogError) throw catalogError;

  // Fetch shop overrides
  const { data: overrides, error: overridesError } = await supabase
    .from('jobs_catalog_overrides')
    .select('*')
    .eq('shop_id', shopId);

  if (overridesError) throw overridesError;

  // Create override map for fast lookup
  const overrideMap = new Map(
    overrides?.map(o => [o.job_id, o]) ?? []
  );

  // Resolve rules for each job
  return catalogJobs?.map(job => {
    const override = overrideMap.get(job.id);
    const resolved = resolveJobBayRules(job, override);

    return {
      ...job,
      ...resolved,
      has_override: override ? true : false,
    };
  }).filter(job => !job.is_disabled) ?? []; // Filter out disabled jobs
}

export interface JobBaySnapshot {
  allowed_bay_types_snapshot: string[];
  required_capabilities_snapshot: string[];
  excluded_bay_types_snapshot: string[];
  bay_requirement_source: 'catalog' | 'manual_override' | 'ai_diagnostics' | 'category_derived';
}

/**
 * Resolve `ticket_jobs.*_snapshot` columns for a batch of newly-created jobs
 * about to be inserted as `ticket_jobs` rows.
 *
 * Without these snapshots, downstream bay gating (`bayGatingValidator` /
 * `globalCascadeBayGating`) sees no requirements and returns "compatible"
 * for any bay — which lets lift-required jobs land in Flat bays. The
 * canonical AddJobDialog single-job path already populates them; this
 * helper exists so the multi-job creation paths (OptiPlaceModal /
 * useCatalystAutoAssign) can do the same in one batched fetch.
 *
 * Resolution order per job:
 *   1. If `catalog_job_id` is provided, read the catalog row + shop override
 *      and run `resolveJobBayRules` (same merge AddJobDialog uses).
 *   2. Else if a `category` key is provided, derive defaults from
 *      `getBayRequirementsForCategory` (covers custom jobs with no catalog
 *      anchor — e.g. suspension/axle work without a specific catalog row).
 *   3. Else return empty arrays (preserves the historical "no requirements
 *      → any bay" behavior).
 */
export async function buildBaySnapshotsForJobs(
  shopId: string,
  jobs: Array<{ catalog_job_id?: string | null; category?: string | null }>,
): Promise<JobBaySnapshot[]> {
  const catalogIds = Array.from(
    new Set(
      jobs
        .map(j => j.catalog_job_id ?? null)
        .filter((id): id is string => !!id),
    ),
  );

  const catalogMap = new Map<string, CatalogJob>();
  const overrideMap = new Map<string, JobOverride>();

  if (catalogIds.length > 0) {
    const { data: catalogRows } = await supabase
      .from('jobs_catalog')
      .select('id, primary_bay_type, allowed_bay_types, required_capabilities, excluded_bay_types')
      .in('id', catalogIds);
    (catalogRows ?? []).forEach(row => catalogMap.set(row.id, row as CatalogJob));

    const { data: overrideRows } = await supabase
      .from('jobs_catalog_overrides')
      .select('job_id, primary_bay_type_override, allowed_bay_types_override, required_capabilities_override, excluded_bay_types_override, disabled')
      .eq('shop_id', shopId)
      .in('job_id', catalogIds);
    (overrideRows ?? []).forEach(row => overrideMap.set(row.job_id, row as unknown as JobOverride));
  }

  return jobs.map(job => {
    if (job.catalog_job_id) {
      const catalogRow = catalogMap.get(job.catalog_job_id);
      if (catalogRow) {
        const resolved = resolveJobBayRules(catalogRow, overrideMap.get(job.catalog_job_id) ?? null);
        return {
          allowed_bay_types_snapshot: resolved.allowed_bay_types,
          required_capabilities_snapshot: resolved.required_capabilities,
          excluded_bay_types_snapshot: resolved.excluded_bay_types,
          bay_requirement_source: resolved.bay_requirement_source,
        };
      }
    }

    if (job.category) {
      const fromCategory = getBayRequirementsForCategory(job.category);
      return {
        allowed_bay_types_snapshot: fromCategory.allowed_bay_types,
        required_capabilities_snapshot: fromCategory.required_capabilities,
        excluded_bay_types_snapshot: [],
        bay_requirement_source: 'category_derived' as const,
      };
    }

    return {
      allowed_bay_types_snapshot: [],
      required_capabilities_snapshot: [],
      excluded_bay_types_snapshot: [],
      bay_requirement_source: 'catalog' as const,
    };
  });
}
